import Button from '../Button'
import { styled } from '../../utils/styled'
import Grid from '../Grid'
import { type Theme } from '../theme'
import { type CSSObject } from '../theme/themeTypes'
import { ATTACHMENT_TILE_REMOVE_CLASS_NAME } from './classnames'
import { type AttachmentTileSize } from './types'

/** The tile is a fixed square by design — the row it sits in wraps instead of scaling. */
export const ATTACHMENT_TILE_SIZE = 64
/** The smaller tile used in a sent message's history. */
export const ATTACHMENT_TILE_SIZE_SM = 40

const getTileSizedStyles = (
  size: AttachmentTileSize,
  theme: Theme
): { width: number; height: number; borderRadius: string } => {
  switch (size) {
    case 'sm':
      return {
        width: ATTACHMENT_TILE_SIZE_SM,
        height: ATTACHMENT_TILE_SIZE_SM,
        borderRadius: theme.borderRadius.medium
      }
    case 'default':
      return { width: ATTACHMENT_TILE_SIZE, height: ATTACHMENT_TILE_SIZE, borderRadius: theme.borderRadius.xLarge }
  }
}

// Intrinsic control dimensions, not spacing: they size the affordances inside
// the tile rather than the gaps between things, so they are plain px like the
// min sizes in Tabs/IconTab rather than `theme.spacing` indices.
const ICON_SIZE_PX = 16
const REMOVE_BUTTON_OFFSET_PX = -10
/** Small enough to leave a short word room at the 64px default size without wrapping. */
const ERROR_LABEL_FONT_SIZE_PX = 10
const ERROR_LABEL_LINE_HEIGHT_PX = 12

/** DotsLoader dimensions per tile size, scaled down from its 40px default to fit the pending overlay. */
const LOADER_SIZE_PX: Record<AttachmentTileSize, { height: number; width: number }> = {
  default: { height: 20, width: 20 },
  sm: { height: 14, width: 14 }
}

export const getLoaderSizePx = (size: AttachmentTileSize): { height: number; width: number } => LOADER_SIZE_PX[size]

const tileShouldForwardProp = (prop: PropertyKey): boolean => prop !== 'size' && prop !== 'isError'

export const StyledTile = styled(Grid, { shouldForwardProp: tileShouldForwardProp })<{
  isError: boolean
  size: AttachmentTileSize
}>(({ theme, isError, size }) => ({
  position: 'relative',
  flexShrink: 0,
  // The red fill already reads as the tile's edge — an outline on top of it
  // just doubles up.
  border: isError ? 'none' : `1px solid ${theme.tokens.background.tonal.superSoft.default}`,
  ...getTileSizedStyles(size, theme),
  [`&:hover .${ATTACHMENT_TILE_REMOVE_CLASS_NAME}`]: { opacity: 1, pointerEvents: 'auto' }
}))

interface ThumbStyleProps {
  theme: Theme
  isError: boolean
  isUnavailable: boolean
  size: AttachmentTileSize
}

const getThumbBackground = ({ theme, isError, isUnavailable }: Omit<ThumbStyleProps, 'size'>): string => {
  if (isError) return theme.tokens.background.semantic.error.tonal.superSoft.default
  // Unavailable gets a background one step darker than the message bubble's
  // own `tonal.soft` fill (see MessageCard's styles) instead of the default
  // `superSoft` — the bubble would otherwise wash it out to almost nothing.
  if (isUnavailable) return theme.tokens.background.tonal.medium.default
  return theme.tokens.background.tonal.superSoft.default
}

const thumbStyles = ({ theme, isError, isUnavailable, size }: ThumbStyleProps): CSSObject => ({
  position: 'absolute',
  inset: 0,
  borderRadius: getTileSizedStyles(size, theme).borderRadius,
  overflow: 'hidden',
  background: getThumbBackground({ theme, isError, isUnavailable }),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1)
})

const thumbShouldForwardProp = (prop: PropertyKey): boolean =>
  prop !== 'isError' && prop !== 'isUnavailable' && prop !== 'size'

export const StyledThumb = styled(Grid, { shouldForwardProp: thumbShouldForwardProp })<{
  isError: boolean
  isUnavailable: boolean
  size: AttachmentTileSize
}>(thumbStyles)

/**
 * The clickable variant, used when `onActivate` is set.
 *
 * It is the thumbnail that becomes the button and not the tile root, because
 * the remove button is the thumbnail's sibling — nesting it inside would give
 * a button within a button. The only hover/focus affordance is the pointer
 * cursor and the focus ring; the picture itself never changes.
 */
export const StyledThumbButton = styled('button', { shouldForwardProp: thumbShouldForwardProp })<{
  isError: boolean
  isUnavailable: boolean
  size: AttachmentTileSize
}>((props): CSSObject => {
  const { theme } = props
  return {
    ...thumbStyles(props),
    padding: 0,
    border: 'none',
    cursor: 'pointer',
    '&:focus-visible': {
      outline: `2px solid ${theme.tokens.border.focus.brand.primary}`,
      outlineOffset: 2
    }
  }
})

export const StyledPreviewImage = styled('img')(() => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block'
}))

/**
 * The icon dims and blurs under the pending overlay, matching the design.
 *
 * `& svg` forces any icon to fill this box via CSS, which wins over the
 * icon's own `width`/`height` attributes regardless of what they are. Without
 * it, an icon like `NoImageIcon` — whose `<svg>` sets `width="100%"
 * height="100%"` directly rather than a fixed pixel size — can fail to
 * resolve that percentage against this flex box's cross-axis (its siblings
 * use `align-items: center`, not `stretch`) and fall back to the browser's
 * default replaced-element size, rendering many times larger than this slot
 * and spilling into neighboring tiles.
 */
export const StyledIconSlot = styled(Grid, { shouldForwardProp: (prop) => prop !== 'isPending' })<{
  isPending: boolean
}>(({ isPending }) => ({
  width: ICON_SIZE_PX,
  height: ICON_SIZE_PX,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  filter: isPending ? 'blur(2px) brightness(0.75)' : 'none',
  transition: 'filter 200ms ease',
  '& svg': {
    width: '100%',
    height: '100%'
  }
}))

export const StyledPendingOverlay = styled(Grid)(() => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // Fixed, not theme-driven: a photo underneath should dim the same way
  // regardless of the app's light/dark theme. `overlay.main` flips per
  // theme — it actually washes *toward white* in dark mode — which made
  // the loading state barely visible over a photo there.
  backgroundColor: 'rgba(15, 12, 8, 0.6)'
}))
export const StyledRemoveButton = styled(Button)(() => ({
  position: 'absolute',
  bottom: REMOVE_BUTTON_OFFSET_PX,
  right: REMOVE_BUTTON_OFFSET_PX,
  opacity: 0,
  // Hidden is non-interactive: without this, a tap on the tile's corner
  // could fire onRemove through a control the user never saw.
  pointerEvents: 'none',
  // Keyboard users never hover, so focus has to reveal it too.
  '&:focus-visible': { opacity: 1, pointerEvents: 'auto' },
  // Touch/stylus devices have no hover to reveal the button, so keep it
  // visible and reachable there instead of hiding it behind an unreachable gesture.
  '@media (hover: none)': { opacity: 1, pointerEvents: 'auto' }
}))

export const StyledRetryButton = styled(Button)(() => ({
  alignSelf: 'center'
}))

export const StyledErrorLabel = styled('span')(({ theme }) => ({
  fontSize: ERROR_LABEL_FONT_SIZE_PX,
  lineHeight: `${ERROR_LABEL_LINE_HEIGHT_PX}px`,
  fontWeight: 500,
  letterSpacing: '0.01em',
  color: theme.tokens.foreground.semantic.error.default,
  whiteSpace: 'nowrap'
}))
